import { useId } from "react";

// Microsoft product marks for the Microsoft 365 integration. Gradient and
// filter ids are prefixed per instance: duplicate ids resolve to the first
// match in the document, which breaks when that copy is hidden.
function useSvgId(): string {
  return `ms${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

// The Microsoft 365 mark (2022). Source: selfh.st/icons (CC BY 4.0), via Iconify "selfhst:microsoft-365".
export function Microsoft365Icon({ className }: { className?: string }) {
  const id = useSvgId();
  return (
    <svg
      className={className}
      viewBox="0 0 512 512"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <radialGradient
        id={`${id}-0`}
        cx="-90.407"
        cy="-6.401"
        r="11.637"
        gradientTransform="rotate(110.528 -1582.507 971.976)scale(33.3657 58.1966)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".064" stopColor="#ae7fe2" />
        <stop offset="1" stopColor="#0078d4" />
      </radialGradient>
      <path
        fill={`url(#${id}-0)`}
        d="m210.4 11.9l-2.6 1.6q-6.3 3.75-11.7 8.4l7.5-5.3h64l11.6 88.1l-58.2 58.2l-58.2 40.4V250c0 32.6 17 62.8 44.9 79.6l61.3 37.1l-129.4 75.5h-25L68.2 414c-27.9-16.9-44.9-47.1-44.9-79.6V177.6c0-32.6 17-62.8 44.9-79.7l139.6-84.5c.9-.5 1.8-1 2.6-1.5"
      />
      <linearGradient
        id={`${id}-1`}
        x1="253.639"
        x2="198.241"
        y1="653.085"
        y2="557.27"
        gradientTransform="translate(0 -278)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#114a8b" />
        <stop offset="1" stopColor="#0078d4" stopOpacity="0" />
      </linearGradient>
      <path
        fill={`url(#${id}-1)`}
        d="m210.4 11.9l-2.6 1.6q-6.3 3.75-11.7 8.4l7.5-5.3h64l11.6 88.1l-58.2 58.2l-58.2 40.4V250c0 32.6 17 62.8 44.9 79.6l61.3 37.1l-129.4 75.5h-25L68.2 414c-27.9-16.9-44.9-47.1-44.9-79.6V177.6c0-32.6 17-62.8 44.9-79.7l139.6-84.5c.9-.5 1.8-1 2.6-1.5"
      />
      <radialGradient
        id={`${id}-2`}
        cx="-95.09"
        cy="32.254"
        r="11.637"
        gradientTransform="rotate(-8.367 -3217.16 -20322.12)scale(31.0503 20.5108)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".134" stopColor="#d59dff" />
        <stop offset="1" stopColor="#5e438f" />
      </radialGradient>
      <path
        fill={`url(#${id}-2)`}
        d="M349.1 197.8v52.1c0 32.6-17 62.8-44.9 79.6L164.6 414c-28.6 17.4-64.1 18-93.2 2l136.4 82.6c29.6 17.9 66.8 17.9 96.4 0L443.8 414c27.9-16.9 44.9-47.1 44.9-79.6v-37.7l-11.6-17.5z"
      />
      <linearGradient
        id={`${id}-3`}
        x1="389.732"
        x2="330.368"
        y1="510.531"
        y2="595.653"
        gradientTransform="translate(0 -278)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#493474" />
        <stop offset="1" stopColor="#8c66ba" stopOpacity="0" />
      </linearGradient>
      <path
        fill={`url(#${id}-3)`}
        d="M349.1 197.8v52.1c0 32.6-17 62.8-44.9 79.6L164.6 414c-28.6 17.4-64.1 18-93.2 2l136.4 82.6c29.6 17.9 66.8 17.9 96.4 0L443.8 414c27.9-16.9 44.9-47.1 44.9-79.6v-37.7l-11.6-17.5z"
      />
      <radialGradient
        id={`${id}-4`}
        cx="-123.182"
        cy="-9.402"
        r="11.637"
        gradientTransform="rotate(194.228 -1265.388 -274.62)scale(24.9228 41.9552)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".058" stopColor="#50e6ff" />
        <stop offset="1" stopColor="#436dcd" />
      </radialGradient>
      <path
        fill={`url(#${id}-4)`}
        d="M443.8 97.9L304.2 13.4C275.5-4 239.8-4.5 210.6 11.8l-2.8 1.7a93.13 93.13 0 0 0-44.9 79.7v110.4l44.9-27.2c29.6-17.9 66.7-17.9 96.4 0l139.6 84.5c27 16.3 43.8 45.2 44.9 76.6c0-1 .1-2 .1-3.1V177.6c-.1-32.6-17.1-62.8-45-79.7"
      />
      <linearGradient
        id={`${id}-5`}
        x1="174.261"
        x2="261.665"
        y1="446.725"
        y2="446.725"
        gradientTransform="translate(0 -278)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#2d3f80" />
        <stop offset="1" stopColor="#436dcd" stopOpacity="0" />
      </linearGradient>
      <path
        fill={`url(#${id}-5)`}
        d="M443.8 97.9L304.2 13.4C275.5-4 239.8-4.5 210.6 11.8l-2.8 1.7a93.13 93.13 0 0 0-44.9 79.7v110.4l44.9-27.2c29.6-17.9 66.7-17.9 96.4 0l139.6 84.5c27 16.3 43.8 45.2 44.9 76.6c0-1 .1-2 .1-3.1V177.6c-.1-32.6-17.1-62.8-45-79.7"
      />
    </svg>
  );
}

// OneDrive. Source: selfh.st/icons (CC BY 4.0), via Iconify "selfhst:microsoft-onedrive".
export function OneDriveIcon({ className }: { className?: string }) {
  const id = useSvgId();
  return (
    <svg
      className={className}
      viewBox="0 0 512 512"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <radialGradient
        id={`${id}-0`}
        cx="-645.674"
        cy="809.09"
        r="11.852"
        gradientTransform="rotate(50.153 -24083.17 346.53)scale(13.616 -22.5845)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#4894fe" />
        <stop offset=".695" stopColor="#0934b3" />
      </radialGradient>
      <path
        fill={`url(#${id}-0)`}
        d="M141.5 137.9C63.2 137.9 4.7 202 .3 273.4C3 288.7 12 319.1 26 317.5c17.5-1.9 61.7 0 99.4-68.2c27.6-49.9 84.2-111.4 16.1-111.4"
      />
      <radialGradient
        id={`${id}-1`}
        cx="-712.098"
        cy="820.107"
        r="11.852"
        gradientTransform="rotate(130.923 -28740.32 -8186.7)scale(58.5497 -43.3858)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".165" stopColor="#23c0fe" />
        <stop offset=".534" stopColor="#1c91ff" />
      </radialGradient>
      <path
        fill={`url(#${id}-1)`}
        d="M123.4 164.5c-26.3 41.7-61.7 101.4-73.7 120.1C35.5 307-2.1 297.5 1 265.5c-.3 2.6-.5 5.2-.7 7.9c-5.1 83.3 60.9 152.4 143 152.4c90.5 0 306.5-112.8 284.6-225.9c-23-66.4-87.8-114.1-161.1-114.1s-120.4 42.3-143.4 78.7"
      />
      <radialGradient
        id={`${id}-2`}
        cx="-778.209"
        cy="790.153"
        r="11.852"
        gradientTransform="rotate(40.014 34029.303 -1714.584)scale(-11.9037 27.25)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#fff" />
        <stop offset=".66" stopColor="#adc0ff" stopOpacity="0" />
      </radialGradient>
      <path
        fill={`url(#${id}-2)`}
        fillOpacity=".4"
        d="M123.4 164.5c-26.3 41.7-61.7 101.4-73.7 120.1C35.5 307-2.1 297.5 1 265.5c-.3 2.6-.5 5.2-.7 7.9c-5.1 83.3 60.9 152.4 143 152.4c90.5 0 306.5-112.8 284.6-225.9c-23-66.4-87.8-114.1-161.1-114.1s-120.4 42.3-143.4 78.7"
      />
      <radialGradient
        id={`${id}-3`}
        cx="-785.013"
        cy="788.94"
        r="11.852"
        gradientTransform="rotate(40.236 26890.23 -5806.634)scale(-13.4172 20.3769)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#033acc" />
        <stop offset="1" stopColor="#368eff" stopOpacity="0" />
      </radialGradient>
      <path
        fill={`url(#${id}-3)`}
        d="M123.4 164.5c-26.3 41.7-61.7 101.4-73.7 120.1C35.5 307-2.1 297.5 1 265.5c-.3 2.6-.5 5.2-.7 7.9c-5.1 83.3 60.9 152.4 143 152.4c90.5 0 306.5-112.8 284.6-225.9c-23-66.4-87.8-114.1-161.1-114.1s-120.4 42.3-143.4 78.7"
      />
      <radialGradient
        id={`${id}-4`}
        cx="-681.337"
        cy="815.638"
        r="11.852"
        gradientTransform="rotate(66.571 -29776.687 2432.916)scale(29.4404 -31.7826)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".593" stopColor="#3464e3" stopOpacity="0" />
        <stop offset="1" stopColor="#033acc" />
      </radialGradient>
      <path
        fill={`url(#${id}-4)`}
        fillOpacity=".6"
        d="M123.4 164.5c-26.3 41.7-61.7 101.4-73.7 120.1C35.5 307-2.1 297.5 1 265.5c-.3 2.6-.5 5.2-.7 7.9c-5.1 83.3 60.9 152.4 143 152.4c90.5 0 306.5-112.8 284.6-225.9c-23-66.4-87.8-114.1-161.1-114.1s-120.4 42.3-143.4 78.7"
      />
      <radialGradient
        id={`${id}-5`}
        cx="-716.29"
        cy="811.473"
        r="11.852"
        gradientTransform="rotate(-45 -50720.304 64446.786)scale(-43.306 67.846)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#4bfde8" />
        <stop offset=".544" stopColor="#4bfde8" stopOpacity="0" />
      </radialGradient>
      <path
        fill={`url(#${id}-5)`}
        fillOpacity=".9"
        d="M123.4 164.5c-26.3 41.7-61.7 101.4-73.7 120.1C35.5 307-2.1 297.5 1 265.5c-.3 2.6-.5 5.2-.7 7.9c-5.1 83.3 60.9 152.4 143 152.4c90.5 0 306.5-112.8 284.6-225.9c-23-66.4-87.8-114.1-161.1-114.1s-120.4 42.3-143.4 78.7"
      />
      <linearGradient
        id={`${id}-6`}
        x1="326.993"
        x2="326.993"
        y1="88.032"
        y2="320.14"
        gradientTransform="matrix(1 0 0 -1 0 514)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#0086ff" />
        <stop offset=".49" stopColor="#0bf" />
      </linearGradient>
      <path
        fill={`url(#${id}-6)`}
        d="M142 425.8s216.2.4 252.9.4c66.5 0 117.1-54.3 117.1-117.7s-51.6-117.4-117.1-117.4s-103.3 49-131.6 102.5C230 356.3 187.6 425 142 425.8"
      />
      <radialGradient
        id={`${id}-7`}
        cx="-651.845"
        cy="793.782"
        r="11.852"
        gradientTransform="rotate(201.632 3701.793 16060.3)scale(-19.6236 37.1605)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#fff" />
        <stop offset=".785" stopColor="#fff" stopOpacity="0" />
      </radialGradient>
      <path
        fill={`url(#${id}-7)`}
        fillOpacity=".4"
        d="M142 425.8s216.2.4 252.9.4c66.5 0 117.1-54.3 117.1-117.7s-51.6-117.4-117.1-117.4s-103.3 49-131.6 102.5C230 356.3 187.6 425 142 425.8"
      />
      <radialGradient
        id={`${id}-8`}
        cx="-725.08"
        cy="837.446"
        r="11.852"
        gradientTransform="rotate(139.243 -13732.089 -7499.93)scale(26.9033 -26.9946)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#4bfde8" />
        <stop offset=".585" stopColor="#4bfde8" stopOpacity="0" />
      </radialGradient>
      <path
        fill={`url(#${id}-8)`}
        fillOpacity=".9"
        d="M142 425.8s216.2.4 252.9.4c66.5 0 117.1-54.3 117.1-117.7s-51.6-117.4-117.1-117.4s-103.3 49-131.6 102.5C230 356.3 187.6 425 142 425.8"
      />
    </svg>
  );
}

// Outlook. Source: selfh.st/icons (CC BY 4.0), via Iconify "selfhst:microsoft-outlook".
export function OutlookIcon({ className }: { className?: string }) {
  const id = useSvgId();
  return (
    <svg
      className={className}
      viewBox="0 0 512 512"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <linearGradient
        id={`${id}-0`}
        x1="55.198"
        x2="337.38"
        y1="321.349"
        y2="496.372"
        gradientTransform="matrix(1 0 0 -1 0 514)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#20a7fa" />
        <stop offset=".4" stopColor="#3bd5ff" />
        <stop offset="1" stopColor="#c4b0ff" />
      </linearGradient>
      <path
        fill={`url(#${id}-0)`}
        d="M362.9 58.3L53.6 254.4l-26.6-42v-36.2c0-13.2 6.7-25.4 17.7-32.6L224.5 27c27.4-17.8 62.7-17.8 90.1 0z"
      />
      <linearGradient
        id={`${id}-1`}
        x1="184.57"
        x2="341.66"
        y1="216.339"
        y2="467.877"
        gradientTransform="matrix(1 0 0 -1 0 514)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#165ad9" />
        <stop offset=".501" stopColor="#1880e5" />
        <stop offset="1" stopColor="#8587ff" />
      </linearGradient>
      <path
        fill={`url(#${id}-1)`}
        d="M311.8 25.2c.9.6 1.9 1.1 2.8 1.7l140.3 91l-348 220.6l-53.4-84.2L308.9 92.1c24.2-15.3 25.2-49.9 2.9-66.9"
      />
      <linearGradient
        id={`${id}-2`}
        x1="321.405"
        x2="146.968"
        y1="252.749"
        y2="394.882"
        gradientTransform="matrix(1 0 0 -1 0 514)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".237" stopColor="#448aff" stopOpacity="0" />
        <stop offset=".792" stopColor="#0032b1" />
      </linearGradient>
      <path
        fill={`url(#${id}-2)`}
        fillOpacity=".2"
        d="M311.8 25.2c.9.6 1.9 1.1 2.8 1.7l140.3 91l-348 220.6l-53.4-84.2L308.9 92.1c24.2-15.3 25.2-49.9 2.9-66.9"
      />
      <linearGradient
        id={`${id}-3`}
        x1="231.401"
        x2="507.028"
        y1="223.028"
        y2="399.429"
        gradientTransform="matrix(1 0 0 -1 0 514)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#1a43a6" />
        <stop offset=".492" stopColor="#2052cb" />
        <stop offset="1" stopColor="#5f20cb" />
      </linearGradient>
      <path
        fill={`url(#${id}-3)`}
        d="M245.8 380.6L107 338.5l295.1-187.1c24.9-15.8 24.8-52-.1-67.7l-1.3-.8l3.8 2.4l89.8 58.3c11 7.2 17.7 19.4 17.7 32.6v35z"
      />
      <linearGradient
        id={`${id}-4`}
        x1="377.318"
        x2="209.831"
        y1="206.836"
        y2="351.767"
        gradientTransform="matrix(1 0 0 -1 0 514)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#0045b9" stopOpacity="0" />
        <stop offset=".67" stopColor="#0d1f69" />
      </linearGradient>
      <path
        fill={`url(#${id}-4)`}
        fillOpacity=".2"
        d="M245.8 380.6L107 338.5l295.1-187.1c24.9-15.8 24.8-52-.1-67.7l-1.3-.8l3.8 2.4l89.8 58.3c11 7.2 17.7 19.4 17.7 32.6v35z"
      />
      <radialGradient
        id={`${id}-5`}
        cx="-714.127"
        cy="764.547"
        r="13.473"
        gradientTransform="matrix(0 -27.0027 -29.2262 0 22614.298 -19259.082)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".568" stopColor="#275ff0" stopOpacity="0" />
        <stop offset=".992" stopColor="#002177" />
      </radialGradient>
      <path
        fill={`url(#${id}-5)`}
        d="M314.6 27c-27.4-17.8-62.7-17.8-90.1 0L44.7 143.6C33.7 150.8 27 163 27 176.2v1.8c.4 13.2 7.4 25.4 18.6 32.5l223.6 140.9l224.1-140.7c11.6-7.3 18.7-20.1 18.7-33.8v34.3v-35c0-13.2-6.7-25.4-17.7-32.6z"
      />
      <linearGradient
        id={`${id}-6`}
        x1="511.976"
        x2="267.477"
        y1="176.383"
        y2="176.383"
        gradientTransform="matrix(1 0 0 -1 0 514)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#4dc4ff" />
        <stop offset=".196" stopColor="#0fafff" />
      </linearGradient>
      <path
        fill={`url(#${id}-6)`}
        d="M229.7 498.4h198c46.5 0 84.2-37.7 84.2-84.2V176.9c0 13.7-7.1 26.5-18.7 33.8L198.7 395.6c-15.9 10-25.5 27.4-25.5 46.2c0 31.3 25.3 56.6 56.5 56.6"
      />
      <radialGradient
        id={`${id}-7`}
        cx="-683.984"
        cy="693.864"
        r="13.474"
        gradientTransform="rotate(-45 6432.18 -13739.32)scale(11.572 -11.572)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".26" stopColor="#0060d1" />
        <stop offset=".908" stopColor="#0383f1" stopOpacity="0" />
      </radialGradient>
      <path
        fill={`url(#${id}-7)`}
        fillOpacity=".4"
        d="M229.7 498.4h198c46.5 0 84.2-37.7 84.2-84.2V176.9c0 13.7-7.1 26.5-18.7 33.8L198.7 395.6c-15.9 10-25.5 27.4-25.5 46.2c0 31.3 25.3 56.6 56.5 56.6"
      />
      <radialGradient
        id={`${id}-8`}
        cx="-707.021"
        cy="765.926"
        r="13.474"
        gradientTransform="rotate(-52.658 14211.265 -41475.1)scale(39.2814 -35.5204)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".732" stopColor="#f4a7f7" stopOpacity="0" />
        <stop offset="1" stopColor="#f4a7f7" />
      </radialGradient>
      <path
        fill={`url(#${id}-8)`}
        fillOpacity=".5"
        d="M229.7 498.4h198c46.5 0 84.2-37.7 84.2-84.2V176.9c0 13.7-7.1 26.5-18.7 33.8L198.7 395.6c-15.9 10-25.5 27.4-25.5 46.2c0 31.3 25.3 56.6 56.5 56.6"
      />
      <radialGradient
        id={`${id}-9`}
        cx="-703.829"
        cy="816.391"
        r="13.474"
        gradientTransform="rotate(123.339 -19114.06 -17818.282)scale(20.7258 -53.7859)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#49deff" />
        <stop offset=".724" stopColor="#29c3ff" />
      </radialGradient>
      <path
        fill={`url(#${id}-9)`}
        d="M310.3 498.3H111.1c-46.5 0-84.2-37.7-84.2-84.2V176.7c0 13.7 7 26.5 18.6 33.8L339.8 396c16.1 10.2 25.9 27.9 25.9 46.9c0 30.6-24.8 55.4-55.4 55.4"
      />
      <linearGradient
        id={`${id}-10`}
        x1="-7.361"
        x2="228.046"
        y1="176.349"
        y2="176.518"
        gradientTransform="matrix(1 0 0 -1 0 514)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".206" stopColor="#6ce0ff" />
        <stop offset=".535" stopColor="#50d5ff" stopOpacity="0" />
      </linearGradient>
      <path
        fill={`url(#${id}-10)`}
        d="M310.3 498.3H111.1c-46.5 0-84.2-37.7-84.2-84.2V176.7c0 13.7 7 26.5 18.6 33.8L339.8 396c16.1 10.2 25.9 27.9 25.9 46.9c0 30.6-24.8 55.4-55.4 55.4"
      />
      <radialGradient
        id={`${id}-11`}
        cx="-662.67"
        cy="804.68"
        r="13.474"
        gradientTransform="rotate(46.924 -26791.81 7728.943)scale(21.0618 -21.0618)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".039" stopColor="#0091ff" />
        <stop offset=".919" stopColor="#183dad" />
      </radialGradient>
      <path
        fill={`url(#${id}-11)`}
        d="M43.8 242.3h128c24.2 0 43.8 19.6 43.8 43.8v128c0 24.2-19.6 43.8-43.8 43.8h-128C19.6 457.9 0 438.3 0 414.1v-128c0-24.2 19.6-43.8 43.8-43.8"
      />
      <radialGradient
        id={`${id}-12`}
        cx="-647.298"
        cy="858.832"
        r="13.474"
        gradientTransform="matrix(0 11.2 12.9188 0 -10987.284 7623.344)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset=".558" stopColor="#0fa5f7" stopOpacity="0" />
        <stop offset="1" stopColor="#74c6ff" />
      </radialGradient>
      <path
        fill={`url(#${id}-12)`}
        fillOpacity=".5"
        d="M43.8 242.3h128c24.2 0 43.8 19.6 43.8 43.8v128c0 24.2-19.6 43.8-43.8 43.8h-128C19.6 457.9 0 438.3 0 414.1v-128c0-24.2 19.6-43.8 43.8-43.8"
      />
      <path
        fill="#fff"
        d="M169.4 349.3c0-9.3-1.5-17.7-4.4-25.2c-2.8-7.7-7-14.6-12.7-20.4c-11.1-11.5-25.5-17.3-43.2-17.3c-18.7 0-33.9 6-45.5 18c-6.2 6.5-10.7 14.2-13.5 22.8c-2.6 7.4-4 15.4-4 24.4c0 8.2 1.3 15.6 3.7 22.4c2.8 8.5 7.2 16.1 13.5 22.4c11.4 11.5 26 17.2 43.7 17.2c18.2 0 33.1-5.9 44.6-17.7c5.8-6 10.2-12.9 13.1-20.8c3.2-7.6 4.7-16.2 4.7-25.8m-29.5 1.5c0 6-.7 11.4-2.2 16.1c-1.5 4.5-3.6 8.3-6.5 11.6c-5.9 6.6-13.7 10-23.5 10c-9.4 0-17.2-3.4-23.2-10.2c-3.8-4.3-6.3-9.7-7.7-16c-.8-3.7-1.3-7.6-1.3-12c0-3.8.4-7.3 1-10.6c1.3-6.8 3.9-12.6 8-17.3c6-7.1 14-10.6 23.9-10.6c9.6 0 17.3 3.4 23 10.3q4.2 4.95 6.3 12c1.5 4.8 2.2 10.4 2.2 16.7"
      />
    </svg>
  );
}

// Outlook Calendar. Source: theSVG (MIT), via Iconify "thesvg-color:outlook-calendar".
export function OutlookCalendarIcon({ className }: { className?: string }) {
  const id = useSvgId();
  return (
    <svg
      className={className}
      viewBox="0 0 22 22"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g fill="none">
        <path
          fill={`url(#${id}-0)`}
          d="m2 7l8.641-.96a3.3 3.3 0 0 1 .718 0L20 7v9.75A3.25 3.25 0 0 1 16.75 20H5.25A3.25 3.25 0 0 1 2 16.75z"
        />
        <path
          fill={`url(#${id}-1)`}
          d="M2 5.25A3.25 3.25 0 0 1 5.25 2h11.5A3.25 3.25 0 0 1 20 5.25V7H2z"
        />
        <g filter={`url(#${id}-3)`}>
          <path
            fill={`url(#${id}-2)`}
            fillRule="evenodd"
            d="M6.75 9.5a1.25 1.25 0 1 0 0 2.5a1.25 1.25 0 0 0 0-2.5m0 4a1.25 1.25 0 1 0 0 2.5a1.25 1.25 0 0 0 0-2.5m3-2.75a1.25 1.25 0 1 1 2.5 0a1.25 1.25 0 0 1-2.5 0M11 13.5a1.25 1.25 0 1 0 0 2.5a1.25 1.25 0 0 0 0-2.5m3-2.75a1.25 1.25 0 1 1 2.5 0a1.25 1.25 0 0 1-2.5 0"
            clipRule="evenodd"
          />
        </g>
        <defs>
          <linearGradient
            id={`${id}-0`}
            x1="11"
            x2="11"
            y1="20"
            y2="8.333"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#0091eb" />
            <stop offset="1" stopColor="#3dcbff" />
          </linearGradient>
          <linearGradient
            id={`${id}-1`}
            x1="10.357"
            x2="20.655"
            y1="-.25"
            y2="12.99"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#0078d4" />
            <stop offset="1" stopColor="#0068c6" />
          </linearGradient>
          <linearGradient
            id={`${id}-2`}
            x1="11"
            x2="11"
            y1="9.5"
            y2="16"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#ebfbff" />
            <stop offset="1" stopColor="#bdf2ff" />
          </linearGradient>
          <filter
            id={`${id}-3`}
            width="12.333"
            height="7.833"
            x="4.833"
            y="9.167"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              result="hardAlpha"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            />
            <feOffset dy=".333" />
            <feGaussianBlur stdDeviation=".333" />
            <feColorMatrix values="0 0 0 0 0.0156863 0 0 0 0 0.286275 0 0 0 0 0.811765 0 0 0 0.32 0" />
            <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_1_520" />
            <feColorMatrix
              in="SourceAlpha"
              result="hardAlpha"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            />
            <feOffset dy=".067" />
            <feGaussianBlur stdDeviation=".033" />
            <feColorMatrix values="0 0 0 0 0.0156863 0 0 0 0 0.286275 0 0 0 0 0.811765 0 0 0 0.24 0" />
            <feBlend in2="effect1_dropShadow_1_520" result="effect2_dropShadow_1_520" />
            <feBlend in="SourceGraphic" in2="effect2_dropShadow_1_520" result="shape" />
          </filter>
        </defs>
      </g>
    </svg>
  );
}
